import React from "react";
import { Chip, Stack, TableCell, TableRow, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import IconButtonIcons from "../../Common/IconButtonIcons";
import TableCommon from "../../Common/Table";
import HasPermission from "../../Common/HasPermission";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { PERMISSIONS } from "../../../helpers/constants";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useDeleteGalleryFolderMutation } from "../../../store/masters/mastersApis";

const GalleryFolderTable = ({
    loading,
    fetching = false,
    count,
    data,
    pagination,
    handlePagination,
    onEdit,
}) => {
    const dispatch = useDispatch();
    const [deleteGalleryFolder] = useDeleteGalleryFolderMutation();

    const columns = [
        { title: "Sr. No.", sort: false, minWidth: 50 },
        { title: "Folder Name", field: "name", sort: true, minWidth: 160 },
        { title: "Activity", field: "activity_data.name", sort: false, minWidth: 180 },
        { title: "Description", field: "description", sort: false, minWidth: 240 },
        { title: "Images", field: "image_count", sort: false, minWidth: 90 },
        { title: "Videos", field: "video_count", sort: false, minWidth: 90 },
        { title: "Created At", field: "createdAt", sort: true, minWidth: 150 },
        { title: "Action", sort: false, minWidth: 120 },
    ];

    const handleDelete = async (_id) => {
        try {
            if (!window.confirm("Are you sure you want to delete this folder?")) {
                return;
            }

            await deleteGalleryFolder({ _id }).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Folder deleted successfully",
                    severity: "success",
                }),
            );
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.message || error?.data?.message || "Failed to delete folder",
                    severity: "error",
                }),
            );
        }
    };

    const renderTableData =
        !loading && data && data.length > 0 ? (
            data.map((row, index) => (
                <TableRow
                    key={row?._id || index}
                    sx={{ "td, th": { borderBottom: 1, borderBottomColor: "divider", padding: "10px" } }}
                >
                    <TableCell align="center">{index + 1 + pagination.pageNo * pagination.limit}</TableCell>
                    <TableCell align="left">
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {row?.name || "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {row?.storage_folder || ""}
                            </Typography>
                        </Stack>
                    </TableCell>
                    <TableCell align="left">
                        <Stack spacing={0.25}>
                            <Typography
                                variant="body2"
                                color={row?.activity_data?.name ? "text.primary" : "text.secondary"}
                            >
                                {row?.activity_data?.name || "Not linked"}
                            </Typography>
                            {row?.activity_data?.activity_id ? (
                                <Typography variant="caption" color="text.secondary">
                                    ID: {row.activity_data.activity_id}
                                </Typography>
                            ) : null}
                        </Stack>
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2" color={row?.description ? "text.primary" : "text.secondary"}>
                            {row?.description || "No description added"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            size="small"
                            color={row?.image_count > 0 ? "success" : "default"}
                            variant={row?.image_count > 0 ? "filled" : "outlined"}
                            label={`${row?.image_count || 0} image${row?.image_count === 1 ? "" : "s"}`}
                            sx={{ borderRadius: "8px" }}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            size="small"
                            color={row?.video_count > 0 ? "primary" : "default"}
                            variant={row?.video_count > 0 ? "filled" : "outlined"}
                            label={`${row?.video_count || 0} video${row?.video_count === 1 ? "" : "s"}`}
                            sx={{ borderRadius: "8px" }}
                        />
                    </TableCell>
                    <TableCell align="center">{handleDateTimeDefault(row?.createdAt)}</TableCell>
                    <TableCell align="center">
                        <HasPermission
                            permissions={[
                                PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.UPDATE,
                                PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.UPDATE,
                            ]}
                            fallback={null}
                        >
                            <IconButtonIcons
                                title="Edit"
                                IconComponent={EditIcon}
                                color="warning"
                                onClick={() => onEdit(row)}
                            />
                        </HasPermission>
                        <HasPermission
                            permissions={[
                                PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.DELETE,
                                PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.DELETE,
                            ]}
                            fallback={null}
                        >
                            <IconButtonIcons
                                title="Delete"
                                IconComponent={DeleteIcon}
                                color="error"
                                onClick={() => handleDelete(row?._id)}
                            />
                        </HasPermission>
                    </TableCell>
                </TableRow>
            ))
        ) : (
            <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
                <TableCell colSpan={8} align="center">
                    Data not found
                </TableCell>
            </TableRow>
        );

    return (
        <TableCommon
            columns={columns}
            tableData={renderTableData}
            count={count}
            loading={loading || fetching}
            pagination={pagination}
            handlePagination={handlePagination}
            maxHeight="40vh"
        />
    );
};

export default GalleryFolderTable;
